// src/hooks/useBoardColors.ts
import { useState } from "react";

export interface ColorScheme {
  name: string;
  lightColor: string;
  darkColor: string;
}

export const DEFAULT_COLOR_SCHEMES: ColorScheme[] = [
  { name: "Classic", lightColor: "#f0d9b5", darkColor: "#b58863" },
  { name: "Blue", lightColor: "#dee3e6", darkColor: "#8ca2ad" },
  { name: "Green", lightColor: "#ffffdd", darkColor: "#86a666" },
  { name: "Brown", lightColor: "#f0e5d8", darkColor: "#9b7653" },
  { name: "Gray", lightColor: "#e8e8e8", darkColor: "#666666" },
];

const STORAGE_KEY_LIGHT = "expandedChess_lightSquareColor";
const STORAGE_KEY_DARK = "expandedChess_darkSquareColor";

export const useBoardColors = () => {
  // Initialize from localStorage or use defaults
  const [lightColor, setLightColorState] = useState<string>(() => {
    const stored = localStorage.getItem(STORAGE_KEY_LIGHT);
    return stored || "#f0d9b5";
  });

  const [darkColor, setDarkColorState] = useState<string>(() => {
    const stored = localStorage.getItem(STORAGE_KEY_DARK);
    return stored || "#b58863";
  });

  /**
   * Set light square color and persist to localStorage
   */
  const setLightColor = (color: string) => {
    setLightColorState(color);
    localStorage.setItem(STORAGE_KEY_LIGHT, color);
    console.log(`🎨 Light square color set to: ${color}`);
  };

  /**
   * Set dark square color and persist to localStorage
   */
  const setDarkColor = (color: string) => {
    setDarkColorState(color);
    localStorage.setItem(STORAGE_KEY_DARK, color);
    console.log(`🎨 Dark square color set to: ${color}`);
  };

  /**
   * Apply a predefined color scheme
   */
  const applyScheme = (scheme: ColorScheme) => {
    setLightColor(scheme.lightColor);
    setDarkColor(scheme.darkColor);
    console.log(`🎨 Applied color scheme: ${scheme.name}`);
  };

  /**
   * Apply a scheme by name
   */
  const applySchemeByName = (schemeName: string) => {
    const scheme = DEFAULT_COLOR_SCHEMES.find((s) => s.name === schemeName);
    if (scheme) {
      applyScheme(scheme);
      return true;
    }
    console.warn(`⚠️ Color scheme not found: ${schemeName}`);
    return false;
  };

  /**
   * Reset to default colors
   */
  const resetToDefault = () => {
    setLightColor("#f0d9b5");
    setDarkColor("#b58863");
    console.log("🎨 Colors reset to default");
  };

  /**
   * Get current color scheme if it matches a preset
   */
  const getCurrentSchemeName = (): string | null => {
    const matching = DEFAULT_COLOR_SCHEMES.find(
      (s) => s.lightColor === lightColor && s.darkColor === darkColor
    );
    return matching ? matching.name : null;
  };

  return {
    // Current colors
    lightColor,
    darkColor,

    // Setters
    setLightColor,
    setDarkColor,

    // Scheme management
    applyScheme,
    applySchemeByName,
    resetToDefault,
    getCurrentSchemeName,

    // Available schemes
    availableSchemes: DEFAULT_COLOR_SCHEMES,
  };
};
